/**
 * État de sauvegarde d'un formulaire (HA-276) : version de saisie, dernière version
 * confirmée par le serveur, file des envois.
 *
 * Autosave périodique, soumission manuelle et vidage de sortie passent par la même file :
 * jamais deux POST du même formulaire en vol, jamais une réponse ancienne qui déclarerait
 * propre une saisie plus récente (seule la VERSION ENVOYÉE est marquée sauvegardée).
 */

interface SaveState {
    version: number;
    saved: number;
    pending: number;
    queue: Promise<unknown>;
}

const states = new WeakMap<HTMLFormElement, SaveState>();

export function formSaveState(form: HTMLFormElement): SaveState {
    let s = states.get(form);
    if (!s) {
        const state: SaveState = { version: 0, saved: 0, pending: 0, queue: Promise.resolve() };
        // `input` seul (comme l'autosave historique) : le pré-remplissage StepForm émet des
        // `change` programmatiques qui ne sont pas une saisie ; selects et cases émettent `input`.
        form.addEventListener("input", () => { state.version += 1; });
        states.set(form, state);
        s = state;
    }
    return s;
}

export function isFormDirty(form: HTMLFormElement): boolean {
    const s = formSaveState(form);
    return s.version > s.saved;
}

export function formVersion(form: HTMLFormElement): number {
    return formSaveState(form).version;
}

export function markFormSaved(form: HTMLFormElement, version: number): void {
    const s = formSaveState(form);
    s.saved = Math.max(s.saved, version);
}

export function isSaveBusy(form: HTMLFormElement): boolean {
    return formSaveState(form).pending > 0;
}

/** Envoi sérialisé : la tâche démarre quand la précédente du même formulaire est terminée. */
export function enqueueSave<T>(form: HTMLFormElement, task: () => Promise<T>): Promise<T> {
    const s = formSaveState(form);
    s.pending += 1;
    const run = s.queue.catch(() => undefined).then(task);
    s.queue = run.catch(() => undefined).finally(() => { s.pending -= 1; });
    return run;
}

/**
 * Succès MÉTIER d'une réponse de formulaire. Une page d'erreur non-JSON arrive en
 * `{success:false,_httpStatus}` (HTTPResponse.getJSON) : jamais un succès.
 */
export function isSaveSuccess(response: Map<any, any> | null | undefined): boolean {
    if (!(response instanceof Map) || response.has("_httpStatus")) { return false; }
    const type = response.get("t") ?? response.get("todo");
    return response.get("ok") === true || response.get("success") === true || type === "success";
}
